// © 2013 - 2023 Tinder, Inc., ALL RIGHTS RESERVED

import { useContext } from 'react';

import {
  THEME_DEFAULT,
  Theme,
  ObsidianTheme,
  GenericObsidianTheme,
  getThemeClassName,
} from '@tinderapp/ui-core';

import { UiEngineThemesContext, UiEngineThemeIdContext } from './UiEngineContext';
import { themeCssVar as theme, ThemeCssVarGetter } from '../../utils/theme';

type ThemeCssAccessorNamespace<T extends Theme = GenericObsidianTheme> = keyof Omit<
  ThemeCssVarGetter<T>,
  'dark'
>;

type ThemeCssAccessorValue<
  N extends ThemeCssAccessorNamespace<T>,
  T extends Theme = GenericObsidianTheme
> = N extends ThemeCssAccessorNamespace<T>
  ? ThemeCssVarGetter<T>[N][keyof ThemeCssVarGetter<T>[N]]
  : never;

// Maps token types to a [name, fallback] tuple
export type DesignTokenDefinitions<T extends Theme = GenericObsidianTheme> = {
  [N in ThemeCssAccessorNamespace]?: Record<
    string,
    [name: string, fallback: ThemeCssAccessorValue<N, T>]
  >;
};

type DesignTokenTuple<
  N extends ThemeCssAccessorNamespace<T>,
  T extends Theme = GenericObsidianTheme
> = N extends ThemeCssAccessorNamespace<T>
  ? [type: N, name: string, fallback: ThemeCssAccessorValue<N, T>]
  : never;

type DesignTokenObject<
  N extends ThemeCssAccessorNamespace<T>,
  T extends Theme = GenericObsidianTheme
> = N extends ThemeCssAccessorNamespace<T>
  ? { type: N; name: string; fallback: ThemeCssAccessorValue<N, T> }
  : never;

// Array of [type, name, fallback] tuples
export type DesignTokenDefinitionsArray<T extends Theme = GenericObsidianTheme> = Array<
  | DesignTokenTuple<ThemeCssAccessorNamespace<T>, T>
  | DesignTokenObject<ThemeCssAccessorNamespace<T>, T>
>;

// Maps token types to a resolved value from the CSS helper
type ResolvedDesignTokens<T extends Theme = GenericObsidianTheme> = {
  [N in ThemeCssAccessorNamespace<T>]?: Record<string, ThemeCssAccessorValue<N, T>>;
};

// Utility class to get correct type definitions for the return value of useDesignTokens
type ExtractResolvedDesignTokens<
  D extends DesignTokenDefinitions<T>,
  T extends Theme = GenericObsidianTheme
> = {
  [N in keyof D]: Record<
    keyof D[N],
    N extends ThemeCssAccessorNamespace<T> ? ThemeCssAccessorValue<N, T> : never
  >;
};

function typeFromCssVarType<T extends Theme = GenericObsidianTheme>(
  type: ThemeCssAccessorNamespace<T>
) {
  switch (type) {
    case 'gradientFill':
      return 'gradient';
    default:
      return type;
  }
}

export function useThemes<T extends Theme = GenericObsidianTheme>(): Record<string, T> {
  return useContext(UiEngineThemesContext) as Record<string, T>;
}

export function useTheme<T extends Theme = GenericObsidianTheme>(themeId?: string): T {
  const themes = useContext(UiEngineThemesContext);
  const currentThemeId = useContext(UiEngineThemeIdContext);
  const theme = themes[themeId || currentThemeId];
  // Fallback to default theme
  return (theme || THEME_DEFAULT) as T;
}

export function useThemeClassName(themeId?: string): string {
  const currentThemeId = useContext(UiEngineThemeIdContext);
  return getThemeClassName(themeId || currentThemeId);
}

export function useDesignToken<
  T extends Theme = ObsidianTheme,
  N extends ThemeCssAccessorNamespace<T> = ThemeCssAccessorNamespace<T>
>(
  cssVarType: N,
  name: string,
  fallback: N extends keyof T ? ThemeCssVarGetter<T>[N][keyof ThemeCssVarGetter<T>[N]] : never
) {
  const currentTheme = useTheme<T>();
  const type = typeFromCssVarType<T>(cssVarType);
  return name in currentTheme[type]
    ? (theme as ThemeCssVarGetter<T>)[cssVarType]?.[name]
    : fallback;
}

export function useDesignTokens<
  T extends Theme = ObsidianTheme,
  D extends DesignTokenDefinitions<T> = DesignTokenDefinitions<T>
>(definitions: D) {
  const currentTheme = useTheme<T>();
  let isValid = true;
  const values: ResolvedDesignTokens<T> = {};
  const fallbacks: ResolvedDesignTokens<T> = {};
  Object.entries(definitions).forEach(([definitionType, definitionsForType]) => {
    const cssVarType = definitionType as ThemeCssAccessorNamespace<T>;
    const type = typeFromCssVarType<T>(cssVarType);
    values[cssVarType] = {};
    fallbacks[cssVarType] = {};
    Object.entries(definitionsForType).forEach(([key, [name, fallback]]) => {
      if (!(type in currentTheme && name in currentTheme[type])) {
        isValid = false;
      }
      values[cssVarType][key] = (theme as ThemeCssVarGetter<T>)[cssVarType]?.[name];
      fallbacks[cssVarType][key] = fallback;
    });
  });
  return (isValid ? values : fallbacks) as ExtractResolvedDesignTokens<typeof definitions, T>;
}

export function useDesignTokensArray<
  T extends Theme = ObsidianTheme,
  D extends DesignTokenDefinitionsArray<T> = DesignTokenDefinitionsArray<T>
>(definitions: D) {
  const currentTheme = useTheme<T>();
  let isValid = true;
  const values: ThemeCssAccessorValue<ThemeCssAccessorNamespace<T>, T>[] = [];
  const fallbacks: ThemeCssAccessorValue<ThemeCssAccessorNamespace<T>, T>[] = [];
  definitions.forEach((definition) => {
    const [definitionType, name, fallback] = Array.isArray(definition)
      ? definition
      : [definition.type, definition.name, definition.fallback];
    const cssVarType = definitionType as ThemeCssAccessorNamespace<T>;
    const type = typeFromCssVarType<T>(cssVarType);
    if (!(type in currentTheme && name in currentTheme[type])) {
      isValid = false;
    }
    values.push((theme as ThemeCssVarGetter<T>)[cssVarType]?.[name]);
    fallbacks.push(fallback);
  });
  return isValid ? values : fallbacks;
}
